import React, { useState, createRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ButtonField, ButtonFieldIcon, Iconsvg, Tooltip, TooltipBody } from '@wk/components-react16';
import { Toast } from '../../services';
import { API_URL } from '../../config/constants';
import HttpService from "../../config/http.service";
import { useSelector, useDispatch } from 'react-redux';
import { showLoading, hideLoading } from "../../store/authReducer";
import GetSmartPrompt from './GetSmartPrompt';
import EditSmartPrompt from './EditSmartPrompt';
import ConfirmPromptScore from '../prompt/model/ConfirmPromptScore';
import ImprovePromptScore from '../prompt/model/ImprovePromptScore';
import AddRunBatch from './AddRunBatch';
import documents from "../../config/constants/sample-documents.json";
import documentsbatch from "../../config/constants/sample-documents-batch.json";
import TryBatch from './TryBatch';
import ResponseTab from '../prompt/ResponseTab';
import BatchResponseTab from './BatchResponseTab';
import { RootState } from '../../store';

const SmartPrompt = () => {
    const navigate = useNavigate();
    const location = useLocation();
	const dispatch = useDispatch();
    const { authProject } = useSelector((state: RootState) => state.auth);
    const storyContainer = createRef<any>();
    let id = 0;
    type PromptType = {
        code: string;
        [key: string]: any;
    } | null;

    const promptObj = {
        id: null,
        name: '',
        text: '',
        promptType: null as PromptType,
        jsonOutput: false,
        score: 0,
        categoryScore: [] as CategoryScore[],
        tags: []
    };

    const [prompt, setPrompt] = useState<typeof promptObj>(promptObj);
    const [isLoad, setIsLoad] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isOpenImproveScore, setIsOpenImproveScore] = useState(false);
    const [isOpenConfirmScore, setIsOpenConfirmScore] = useState(false);
    const [isOpenConfirmScoreQuestion, setIsOpenConfirmScoreQuestion] = useState(false);
    const [previousPrompt, setPreviousPrompt] = useState('');
    type CategoryScore = {
        id: number | null;
        categoryScoreTypeId: number;
        categoryScoreTypeCode: string;
        categoryScoreTypeName: string;
        categoryScoreTypeDescription: string;
        partialPrompt: string;
        rationale: string;
        suggestion: string;
        score: number;
    };
    const [promptCategoryScore, setPromptCategoryScore] = useState<CategoryScore[]>([]);
    const [isScoreShow, setIsScoreShow] = useState(false);
    const [isOpenRunBatch, setIsOpenRunBatch] = useState(false);
    const [promptTypeList, setPromptTypeList] = useState([] as any);
    // Get Smart Prompt intialization
    const [fileList, setFileList] = useState([] as any);
    const [documentText, setDocumentText] = useState('');
    const [summary, setSummary] = useState('');
    const [documentFile, setDocumentFile] = useState('');
    const [documentList, setDocumentList] = useState(documents as any);
    const [taxonomyTree, setTaxonomyTree] = useState([] as any);
    // Run Batch intialization
    const [validationsList, setValidationsList] = useState([] as any);
    const [tryRunSelectedIndex, setTryRunSelectedIndex] = useState(0);
    const [tryNowdocumentText, setTryNowdocumentText] = useState('');
    const [tryNowFileList, setTryNowFileList] = useState([] as any);
    const [tryNowdocumentBatchList, setTryNowdocumentBatchList] = useState(documentsbatch as any);
    const [runPromptResponse, setRunPromptResponse] = useState([] as any);
    const [validationResponse, setValidationResponse] = useState({} as any);
    const [inputOfRunPromptResponse, setInputOfRunPromptResponse] = useState({} as any);
    
    const handleImproveScore = () => {
        setIsOpenConfirmScoreQuestion(true);
    }

    const handleRunBatch = () => {
        setIsOpenRunBatch(true);
    }

    const back = () => {
        if (activeIndex == 1) {
            resetValidationsList();
            setTryRunSelectedIndex(0);
            setTryNowdocumentText('');
            setTryNowFileList([]);
            resetDocumentBatchList();
            setRunPromptResponse([]);
            setValidationResponse({});
            setInputOfRunPromptResponse({});
        }
        if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }
    }

    const resetDocumentBatchList = () => {
        tryNowdocumentBatchList.forEach((item: any) => {
            item.isSelected = false;
            item.status = true;
        });
        setTryNowdocumentBatchList([...tryNowdocumentBatchList]);
    }

    const resetValidationsList = () => {
        validationsList.forEach((item: any) => item.isSelected = false);
        setValidationsList([...validationsList]);
    }

    const onNext = () => {
        setActiveIndex(activeIndex + 1);
    }

    const onPrevious = () => {
        if (activeIndex === 2) {
            setRunPromptResponse({});
            setActiveIndex(activeIndex - 1);
        }
    }

    const getCheckedNodes = (node: any) => {
        let checkedItems: any = [];

        if (node.isChecked) {
            checkedItems.push({
                id: node.id,
                name: node.name,
            });
        }

        if (node.children && node.children.length > 0) {
            node.children.forEach((child: any) => {
                checkedItems = checkedItems.concat(getCheckedNodes(child));
            });
        }
        return checkedItems;
    };

    const checkTopLevelSelections = (nodes: any) => {
        return nodes.map((category: any) => {
            let checkedItems: any = [];

            category.children.forEach((child: any) => {
                checkedItems = checkedItems.concat(getCheckedNodes(child));
            });

            return {
                category: category.name,
                hasChecked: checkedItems.length > 0,
                checkedItems
            };
        });
    };

    const getValidatePrompt = async (tree: any) => {
        const result: any = checkTopLevelSelections(tree).filter((item: any) => item.hasChecked);
        const payload: any = {
            projectId: authProject?.id,
            promptId: prompt.id ? prompt.id : null,
            promptTypeCode: prompt?.promptType?.code,
            catCodesMap: {}
        };
        result.forEach((item: any) => {
            if (item.hasChecked) {
                payload.catCodesMap[item.category] = item.checkedItems.map((checkedItem: any) => checkedItem.id);
            }
        });
        if (result.length > 0) {
            try {
                const res = await HttpService.postRequest(`${API_URL.VALIDATE_PROMPT}`, payload);
                return res;
            } catch (err: any) {
                return err.message;
            }
        }
    }

    const validateTaxonomy = async (tree: any) => {
        const result = checkTopLevelSelections(tree);
        let stg = result.filter((item: any) => !item.hasChecked).map((item: any) => item.category).join(', ');
        if (stg.length > 0) {
            Toast(`At least one leaf node must be selected from each category: ${stg}`, "error");
            return true;
        } else {
            const res: any = await getValidatePrompt(taxonomyTree);
            if (res && res.message) {
                Toast(res.message, "error");
                return true;
            }
        }
        return false;
    };

    const editSmartPrompt = (data: any) => {
        let promptType: any = null;
        if (data?.prompt_type) {
            promptType = promptTypeList.find((item: any) => item.code.toUpperCase() === data.prompt_type.toUpperCase());
        }
        setPrompt({
            ...prompt,
            name: data.prompt_name,
            text: data.prompt_instructions,
            promptType: promptType ? promptType : null,
        });
        setActiveIndex(1);
        resetValidationsList();
        tryNowdocumentBatchList.forEach((item: any) => item.isSelected = false );
        setTryNowdocumentBatchList([...tryNowdocumentBatchList]);
    }

    const handlePrompt = async () => {
        if ((!documentText || !documentText.trim()) && (fileList.length == 0)
            && (!documentFile || !documentFile.trim())) {
            Toast('Please select document file or enter text or upload document', "error");
            return;
        }
        if (!summary || !summary.trim()) {
            Toast('Please enter prompt summary text', "error");
            return;
        }
        const formData = new FormData();
        formData.append('summary', summary);
        formData.append('id', "test_id");
        formData.append('type', "SUMMARIZATION");
        formData.append('documentText', documentText);
        if (fileList.length > 0) {
            formData.append('document', fileList[0].nativeFile);
        }
        if (documentFile) {
            formData.append('documentFile', documentFile);
        }
        dispatch(showLoading());
        HttpService.postRequestFormData(API_URL.POST_PROCESS_SUMMARY, formData).then(
            (res: any) => {
                if (res.data) {
                    editSmartPrompt(res.data);
                }
                dispatch(hideLoading());
            },
            (err: any) => {
                Toast(err.message, "error");
                dispatch(hideLoading());
            }
        )
    }

    const handleTryNow = async () => {
        if (!isValidInput(prompt)) return;
        if (await validateTaxonomy(taxonomyTree)) return;
        if (tryRunSelectedIndex == 0) {
            if (!tryNowdocumentText || !tryNowdocumentText.trim()) {
                Toast('Please enter document text', "error");
                return;
            }
        } else if (tryRunSelectedIndex == 1) {
            if (tryNowFileList.length == 0) {
                Toast('Please upload document', "error");
                return;
            }
        } else {
            if (tryNowdocumentBatchList.filter((item: any) => item.isSelected).length === 0) {
                Toast("Please select atleast one document.", "error")
                return;
            }
        }
        const formData = new FormData();
        formData.append('type', prompt.promptType?.code ?? '');
        formData.append('prompt', prompt.text);
        formData.append('outputType', prompt.jsonOutput.toString());
        formData.append('documentText', tryNowdocumentText);
        formData.append('projectId', authProject?.id);
        if (tryNowFileList.length > 0) {
            for (let file of tryNowFileList) {
                formData.append('document', file.nativeFile);
            }
        }
        dispatch(showLoading());
        const validationsIds = validationsList.filter((item: any) => item.isSelected);
        if (tryRunSelectedIndex == 2) {
            const selectedDocuments = tryNowdocumentBatchList.filter((item: any) => item.isSelected);
            //const selectedDocumentsList = selectedDocuments.map((item: any) => ({ ...item, validations: validationsIds }));

            const selectedDocumentsList = selectedDocuments.map((item: any) => {
                item.validations.forEach((itm: any) => {
                    itm.isSelected = validationsIds.some((validation: any) => validation.id === itm.id)
                });
                return item;
            });

            setRunPromptResponse([...selectedDocumentsList]);
            dispatch(hideLoading());
        } else {
            // Capture input data for export
            const inputData: any = {
                type: prompt.promptType?.code,
                prompt: prompt.text,
                outputType: prompt.jsonOutput.toString(),
                documentText: tryNowdocumentText,
            };
            
            if (tryNowFileList.length > 0) {
                const fileNames = [];
                for (let file of tryNowFileList) {
                    formData.append('document', file.nativeFile);
                    fileNames.push(file.nativeFile.name);
                }
                inputData.documents = fileNames;
            }
            
            setInputOfRunPromptResponse(inputData);
            HttpService.postRequestFormData(API_URL.RUN_DOCUMENT, formData).then(
                (res: any) => {
                    if (res.status == 200 && res.data) {
                        setRunPromptResponse(res.data ? res.data : []);

                        if (validationsIds.length > 0) {
                            runValidation(res.data[0]?.response, validationsIds);
                        }
                    }
                    dispatch(hideLoading());
                },
                (err: any) => {
                    Toast(err.message, "error");
                    dispatch(hideLoading());
                }
            )
        }
    }

    const runValidation = async (data: any, validationsIds: any) => {
        setValidationResponse({});
        const payload: any = {
            llmResponse: data,
            validationCases: validationsIds,//.map((item: any) => item.name)
            promptInstructions: prompt.text
        }
        dispatch(showLoading());
        HttpService.postRequest(API_URL.RUN_VALIDATION, payload).then(
            (res: any) => {
                if (res.status == 200 && res.data) {
                    setValidationResponse(res.data);
                }
                dispatch(hideLoading());
            },
            (err: any) => {
                Toast(err.message, "error");
                dispatch(hideLoading());
            }
        )
    }

    const isValidInput = (prompt: any) => {
        if (!prompt.name || !prompt.name.trim()) {
            Toast('Please enter prompt name', "error");
            return false;
        }
        if (!prompt.text || !prompt.text.trim()) {
            Toast('Please enter prompt text', "error");
            return false;
        }
        if (!prompt.promptType || !prompt.promptType.code || !prompt.promptType.code.trim()) {
            Toast('Please select prompt type', "error");
            return false;
        }
        return true;
    }

    const savePrompt = async () => {
        if (!isValidInput(prompt)) return;
        if (await validateTaxonomy(taxonomyTree)) return;
        let catCodesMap: any = {}
        const result: any = checkTopLevelSelections(taxonomyTree).filter((item: any) => item.hasChecked);
        result.forEach((item: any) => {
            if (item.hasChecked) {
                catCodesMap[item.category] = item.checkedItems.map((checkedItem: any) => checkedItem.id);
            }
        });

        const payload: any = {
            projectId: authProject.id,
            id: prompt.id,
            promptTypeId: prompt.promptType?.id,
            promptTypeCode: prompt.promptType?.code,
            taxonomyIds: prompt?.tags?.map((item: any) => item.id),
            name: prompt.name,
            text: prompt.text,
            jsonOutput: prompt.jsonOutput,
            score: prompt.score,
            categoryScore: promptCategoryScore,
            catCodesMap: catCodesMap
        }
        dispatch(showLoading());
        HttpService.postRequest(API_URL.ADD_PROMPT, payload).then(
            (res: any) => {
                Toast(typeof prompt.id === 'number' && prompt.id > 0 ? 'Prompt updated successfully.' : 'Prompt added successfully.', "success");
                setIsLoad(true);
                if (location.pathname != '/prompt') navigate("/prompt");
                dispatch(hideLoading());
            },
            (err: any) => {
                Toast(err.message, "error");
                dispatch(hideLoading());
            }
        )
    }

    const handlePromptScore = async (status: boolean) => {
        if (!isValidInput(prompt)) return;
        if (status) {
            if (await validateTaxonomy(taxonomyTree)) return;
        }
        if (previousPrompt?.trim() == prompt.text?.trim()) {
            setIsOpenConfirmScore(true);
            setIsScoreShow(true);
            if (status && activeIndex < 2) {
                onNext();
            }

        } else {
            getPromptScore(status);
        }
    }

    const getPromptScore = (status: boolean) => {
        if (!prompt.text || !prompt.text.trim()) {
            Toast('Please enter prompt text', "error");
            return;
        }
        if (!prompt.promptType || !prompt.promptType.code || !prompt.promptType.code.trim()) {
            Toast('Please select prompt type', "error");
            return;
        }
        if(!promptCategoryScore || promptCategoryScore.length == 0) {
            Toast('Category score types are not defined for this project. Please contact administrator.', "error");
            return;
        }

        const payload: any = {
            promptType: prompt.promptType.code,
            promptInstructions: prompt.text,
            categoryScore: promptCategoryScore
        }
        dispatch(showLoading());
        HttpService.postRequest(API_URL.IMPROVED_PROMPT_SCORE, payload).then(
            (res: any) => {
                if (res.data){
                    setPrompt({
                        ...prompt,
                        score: res.data.totalScore
                    });
                    if (!status) {
                        setIsOpenConfirmScore(true);
                    }
                    setIsScoreShow(true);
                    setPreviousPrompt(prompt.text);
                    setPromptCategoryScore(res.data.categoryScore);
                } else {
                    setIsScoreShow(true);
                    setPrompt({
                        ...prompt,
                        score: 0
                    });
                    Toast('There is an error occurred for getting prompt score, please try again.', "error");
                }
                dispatch(hideLoading());
                if (status && activeIndex < 2) {
                    onNext();
                }
            },
            (err: any) => {
                Toast(err.message, "error");
                setIsScoreShow(true);
                setPrompt({
                    ...prompt,
                    score: 0
                });
                dispatch(hideLoading());
                if (status && activeIndex < 2) {
                    onNext();
                }
            }
        )
    }

    const getValidationRule = () => {
        HttpService.getRequest(`${API_URL.GET_VALIDATION}?projectId=${authProject.id}`).then(
            (res: any) => {
                const list = res.sort((a: any, b: any) => b.updated - a.updated)
                    .map((item: any) => ({ ...item, label: item.name, isSelected: false, status: false }));
                setValidationsList(list);
            },
            (err: any) => {
                Toast(err.message, "error");
            },
        )
    }

    const getPromptType = () => {
        HttpService.getRequest(`${API_URL.GET_PROMPT_TYPE_BY_PROJECT_ID_APPLICABLE}/${authProject.id}`).then(
            (res: any) => {
                setPromptTypeList(res);
            },
            (err: any) => {
                Toast(err.message, "error");
            },
        )
    }

    const getCategoryScoreType = () => {
        HttpService.getRequest(`${API_URL.GET_CATEGORY_SCORE_TYPE_BY_PROJECT_ID_APPLICABLE}/${authProject.id}`).then(
            (res: any) => {
                const list = res.map((item: any) => ({ id: null, categoryScoreTypeId: item.id, categoryScoreTypeCode: item.code, categoryScoreTypeName: item.name, categoryScoreTypeDescription: item.description, partialPrompt: '', rationale: '', suggestion: '', score: 0 }));

                const updatedList = list.map((item: any) => {
                    const matchedItem = (prompt.categoryScore ?? []).find((itm: any) => itm.categoryScoreTypeCode === item.categoryScoreTypeCode);
                    if (matchedItem) {
                        return {
                            ...item,
                            partialPrompt: matchedItem?.partialPrompt,
                            rationale: matchedItem?.rationale,
                            suggestion: matchedItem?.suggestion,
                            score: matchedItem?.score
                        };
                    }
                    return item;
                });
                setPromptCategoryScore(updatedList);
            },
            (err: any) => {
                Toast(err.message, "error")
            },
        )
    }

    const getTaxonomyTreeByProject = () => {
        HttpService.getRequest(`${API_URL.GET_TAXONOMY_TREE_BY_PROJECT_ID}/${authProject.id}`).then(
            (res: any) => {
                setTaxonomyTree(addIsCheckedKey(res, []));
            },
            (err: any) => {
                Toast(err.message, "error");
            },
        )
    }

    const addIsCheckedKeyInTree = (tree: any, selectedItem: any) => {
        return tree.map((item: any) => {
            const isChecked = selectedItem?.some((itm: any) => itm == item.id)
            if (item.children && item.children.length > 0) {
                return {
                    ...item,
                    isChecked: isChecked,
                    children: addIsCheckedKeyInTree(item.children, selectedItem),
                };
            }

            return {
                ...item,
                isChecked: isChecked
            };
        });
    };

    const addIsCheckedKey = (tree: any, selectedItem: any) => {
        return tree.map((item: any) => {
            if (item.children && item.children.length > 0) {
                return {
                    ...item,
                    children: addIsCheckedKeyInTree(item.children, selectedItem)
                };
            }
        });
    };

    const exportPromptOutput = () => {
		if (runPromptResponse?.data?.length == 0) {
			Toast('No prompt output available to export', "error");
			return;
		}
		
		const exportData = {
			input: inputOfRunPromptResponse,
			output: runPromptResponse
		};
		
		const jsonString = JSON.stringify(exportData, null, 2);
		const blob = new Blob([jsonString], { type: 'application/json' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `Prompt_Output_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
		document.body.appendChild(link);
		link.click();
		
		// Cleanup
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
		
		Toast('Prompt output exported successfully', 'success');
	}

    useEffect(() => {
        resetValidationsList();
        setTryRunSelectedIndex(0);
        setTryNowdocumentText('');
        setTryNowFileList([]);
        resetDocumentBatchList();
        setRunPromptResponse({});
        setValidationResponse({});
    }, []);

    useEffect(() => {
        if (authProject && authProject.id) {
            getValidationRule();
            getTaxonomyTreeByProject();
            getPromptType();
            getCategoryScoreType();
        }
    }, [authProject]);

    useEffect(() => {
        if (isOpenImproveScore) {
            if (prompt.categoryScore) {
                setPreviousPrompt(prompt.text);
                setPromptCategoryScore(prompt.categoryScore);
                setIsOpenConfirmScore(true);
                setIsScoreShow(true);
            } else {
                getPromptScore(false);
            }
            setIsOpenImproveScore(false);
        }
    }, [isOpenImproveScore, prompt]);

    return (
        <div className="p-20" style={{ height: '100%' }}>
            {activeIndex != 0 ? (
                <div className={`${(Object.keys(runPromptResponse).length > 0 && activeIndex != 2) ? '' : 'mt-0 card-maxwidth'}`}>
                    <ButtonField iconPosition={'left'} mode={'ghost'} size={'small'}>
                        <button
                            type="button"
                            data-e2e="cg-previous-button"
                            onClick={back}>
                            <Iconsvg name="arrow-left"></Iconsvg>
                            Back{' '}
                        </button>
                    </ButtonField>
                </div>
            ): null}
            <div className="wk-row ml-0 mr-0">
                {activeIndex == 0 && <div className={`p-20 custom-fs-field fade-in card-maxwidth card-primary mt-0 mb-20`}>
                    <GetSmartPrompt data={{ documentFile, setDocumentFile, documentText, setDocumentText, fileList, setFileList, summary, setSummary, documentList, tryNowdocumentBatchList, setTryNowdocumentBatchList }} />
                        <div className='text-right'>
                            <ButtonField iconPosition={'right'} mode={'ghost'}>
                                <button type="button"
                                    onClick={() => handlePrompt()} className='mr-10'>
                                    Get Smart Prompt
                                </button>
                            </ButtonField>
                        </div>
                    </div>
                }
                {activeIndex == 1 && (<>
                    <div className={`fade-in custom-fs-field ${runPromptResponse.length > 0 ? 'wk-col-4 pl-0' : 'card-maxwidth card-primary pl-15 pb-15 mb-20'}`}>
                        <div className="pr-15 pt-15 pb-15 custom-fs-field">
                            <EditSmartPrompt data={{ prompt, setPrompt, handlePromptScore, isScoreShow, promptTypeList, promptCategoryScore, taxonomyTree, setTaxonomyTree, validateTaxonomy }} />
                            <TryBatch data={{ tryNowdocumentText, setTryNowdocumentText, tryNowdocumentBatchList, setTryNowdocumentBatchList, tryNowFileList, setTryNowFileList, tryRunSelectedIndex, setTryRunSelectedIndex, validationsList, setValidationsList, setIsOpenRunBatch }} />
                            <div className='text-right'>
                                {isScoreShow ? <ButtonField iconPosition={'right'} mode={'ghost'}>
                                    <button type="button"
                                        onClick={() => handleImproveScore()} className='mr-10'>
                                        Enhance Score
                                    </button>
                                </ButtonField> : null}
                                <ButtonField iconPosition={'right'} mode={'ghost'}>
                                    <button type="button"
                                        onClick={() => handlePromptScore(true)} className='mr-10'>
                                        Get Score & Save Prompt
                                    </button>
                                </ButtonField>
                                <ButtonField>
                                    <button type="button"
                                        onClick={() => handleTryNow()}>
                                        Run
                                    </button>
                                </ButtonField>
                            </div>
                        </div>
                    </div>
                    {runPromptResponse.length > 0 ? (
                        <div className='wk-col-8 pr-0 border-start border-primary-subtle fade-in'>
                            <div className="p-20 pr-0 pt-0">
                                {tryRunSelectedIndex == 2 ? (
                                    <BatchResponseTab data={{ runPromptResponse }} />
                                ) : (runPromptResponse.length > 0 ? (
                                        <>
                                            <div className="text-right">
                                                <ButtonFieldIcon>
                                                    <button id='export-prompt-output' type="button" onClick={() => exportPromptOutput()}>
                                                        <Iconsvg name="export" color='#020102ff'></Iconsvg>
                                                    </button>
                                                </ButtonFieldIcon>
											</div>
											<Tooltip
												targetSelector="#export-prompt-output"
												identifier="tooltip-top"
												position="top"
												>
												<TooltipBody slot="tooltipBody">Export Prompt Output JSON</TooltipBody>
											</Tooltip>
                                            <ResponseTab data={{ runPromptResponse, setRunPromptResponse, validationResponse }} />
                                        </>
                                    ) : null
                                )}
                            </div>
                        </div>
                    ) : null}
                </>)}
                {activeIndex == 2 && (
                    <div className="p-15 card-maxwidth card-primary mb-20 fade-in custom-fs-field">
                        <EditSmartPrompt data={{ prompt, setPrompt, handlePromptScore, isScoreShow, promptTypeList, promptCategoryScore, taxonomyTree, setTaxonomyTree, validateTaxonomy }} />
                        <div className='text-right'>
                            <ButtonField>
                                <button type="button"
                                    onClick={() => savePrompt()}>
                                    Submit
                                </button>
                            </ButtonField>
                        </div>
                    </div>
                )}
            </div>
            {isOpenConfirmScore && <ConfirmPromptScore data={{ isOpenConfirmScore, setIsOpenConfirmScore, setIsOpenConfirmScoreQuestion, promptCategoryScore }} />}
            {isOpenConfirmScoreQuestion && <ImprovePromptScore data={{ isOpenConfirmScoreQuestion, setIsOpenConfirmScoreQuestion, promptCategoryScore, setPromptCategoryScore, prompt, setPrompt, onPrevious, activeIndex }} />}
            {isOpenRunBatch && <AddRunBatch data={{ isOpenRunBatch, setIsOpenRunBatch, tryNowdocumentBatchList, setTryNowdocumentBatchList, documentFile }} />}
        </div>
    );
};

export default SmartPrompt;